import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LuPlus, LuPencil, LuTrash2 } from 'react-icons/lu';
import { Icon } from '../../lib/icons';
import { isAuthenticated } from '../../lib/auth';
import api from '../../lib/api';
import AdminLayout from '../../components/AdminLayout';
import styles from './Dashboard.module.css';

interface StockAccount {
  id: string;
  country: string;
  withAccount: boolean;
  username: string;
  password: string;
  email: string | null;
  phone: string | null;
  notes: string | null;
  status: 'AVAILABLE' | 'SOLD' | 'RESERVED' | 'INVALID';
  soldAt: string | null;
  orderId: string | null;
  createdAt: string;
}

interface StockStats {
  [country: string]: {
    withAccount: {
      available: number;
      sold: number;
      reserved: number;
      invalid: number;
      total: number;
    };
    withoutAccount: {
      available: number;
      sold: number;
      reserved: number;
      invalid: number;
      total: number;
    };
  };
}

const Stock: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState<StockAccount[]>([]);
  const [stats, setStats] = useState<StockStats>({});
  const [selectedCountry, setSelectedCountry] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [csvInput, setCsvInput] = useState('');
  const [addCountry, setAddCountry] = useState('FR');
  const [adminRole, setAdminRole] = useState<string>('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState<StockAccount | null>(null);
  const [editStatus, setEditStatus] = useState<string>('AVAILABLE');
  const [editNotes, setEditNotes] = useState<string>('');

  const fetchData = React.useCallback(async () => {
    try {
      const [accountsRes, statsRes] = await Promise.all([
        api.get('/stock', {
          params: {
            country: selectedCountry !== 'all' ? selectedCountry : undefined,
            status: selectedStatus !== 'all' ? selectedStatus : undefined,
          },
        }),
        api.get('/stock/stats'),
      ]);
      setAccounts(accountsRes.data.accounts || []);
      setStats(statsRes.data.stats || {});
    } catch (err) {
      console.error('Failed to fetch stock data:', err);
    }
  }, [selectedCountry, selectedStatus]);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/admin/login');
      return;
    }
    
    // Fetch admin role
    api.get('/auth/me')
      .then((res) => setAdminRole(res.data.user.role))
      .catch((err) => console.error('Failed to fetch user:', err));
    
    fetchData();
  }, [navigate, fetchData]);

  const handleAddStock = async () => {
    try {
      await api.post('/stock/bulk', {
        csv: csvInput,
        country: addCountry,
        withAccount: true,
      });
      setCsvInput('');
      setShowAddModal(false);
      fetchData();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to add stock');
    }
  };

  const handleEditAccount = (account: StockAccount) => {
    setEditingAccount(account);
    setEditStatus(account.status);
    setEditNotes(account.notes || '');
    setShowEditModal(true);
  };

  const handleUpdateAccount = async () => {
    if (!editingAccount) return;
    try {
      await api.put(`/stock/${editingAccount.id}`, {
        status: editStatus,
        notes: editNotes,
      });
      setShowEditModal(false);
      setEditingAccount(null);
      fetchData();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to update account');
    }
  };

  const handleDeleteAccount = async (accountId: string) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce compte du stock ?')) {
      return;
    }
    try {
      await api.delete(`/stock/${accountId}`);
      fetchData();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to delete account');
    }
  };

  return (
    <AdminLayout>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '28px' }}>
        <h1 style={{ fontSize: '1.375rem' }}>{t('admin.stock' as string) || 'Stock Management'}</h1>
        {/* Hide "Add Stock" button for MANAGER role */}
        {adminRole !== 'MANAGER' && (
          <button
            onClick={() => setShowAddModal(true)}
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '14px',
            }}
          >
            <Icon icon={LuPlus} size={20} />
            Ajouter des comptes
          </button>
        )}
      </div>

        {/* Stats Cards */}
        <div className={styles.statsGrid}>
          {Object.entries(stats).map(([country, data]) => {
            const totalAvailable = data.withAccount.available + data.withoutAccount.available;
            return (
              <div key={country} className={styles.statCard}>
                <div className={styles.statValue} style={{ fontSize: '32px', fontWeight: '700' }}>
                  {country === 'FR' ? '🇫🇷' : country === 'US' ? '🇺🇸' : ''} {country}
                </div>
                <div className={styles.statLabel} style={{ fontSize: '14px', marginTop: '8px' }}>
                  <span style={{ color: '#48bb78', fontWeight: '600' }}>{totalAvailable}</span> disponibles
                </div>
              </div>
            );
          })}
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
          <select
            value={selectedCountry}
            onChange={(e) => setSelectedCountry(e.target.value)}
            style={{
              padding: '10px 16px',
              borderRadius: '8px',
              border: '1px solid #2d3748',
              background: '#1a202c',
              color: 'white',
              fontSize: '14px',
            }}
          >
            <option value="all">Tous les pays</option>
            <option value="FR">🇫🇷 France</option>
            <option value="US">🇺🇸 USA</option>
          </select>

          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            style={{
              padding: '10px 16px',
              borderRadius: '8px',
              border: '1px solid #2d3748',
              background: '#1a202c',
              color: 'white',
              fontSize: '14px',
            }}
          >
            <option value="all">Tous les statuts</option>
            <option value="AVAILABLE">✅ Disponible</option>
            <option value="SOLD">💰 Vendu</option>
            <option value="RESERVED">🔒 Réservé</option>
            <option value="INVALID">❌ Invalide</option>
          </select>
        </div>

        {/* Accounts Table */}
        <div className={styles.tableSection}>
          <div className={styles.tableHeader}>
            <h2>Comptes en stock ({accounts.length})</h2>
          </div>
          {accounts.length > 0 ? (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Pays</th>
                  {/* Hide sensitive columns for MANAGER role */}
                  {adminRole !== 'MANAGER' && (
                    <>
                      <th>Username</th>
                      <th>Password</th>
                      <th>2FA</th>
                    </>
                  )}
                  <th>Statut</th>
                  <th>Vendu le</th>
                  {/* Hide actions for MANAGER role */}
                  {adminRole !== 'MANAGER' && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {accounts.map((account) => (
                  <tr key={account.id}>
                    <td style={{ fontWeight: '600' }}>{account.country === 'FR' ? '🇫🇷' : '🇺🇸'} {account.country}</td>
                    {/* Hide sensitive data for MANAGER role */}
                    {adminRole !== 'MANAGER' && (
                      <>
                        <td style={{ fontFamily: 'monospace', fontSize: '13px', fontWeight: '600', color: '#48bb78' }}>{account.username}</td>
                        <td style={{ fontFamily: 'monospace', fontSize: '13px' }}>{account.password}</td>
                        <td style={{ fontFamily: 'monospace', fontSize: '12px', color: '#805ad5', fontWeight: '600' }}>{account.phone || '-'}</td>
                      </>
                    )}
                    <td>
                      <span className={`${styles.statusBadge} ${styles[account.status.toLowerCase()]}`}>
                        {account.status === 'AVAILABLE' ? '✅ Disponible' : 
                         account.status === 'SOLD' ? '💰 Vendu' : 
                         account.status === 'RESERVED' ? '🔒 Réservé' : '❌ Invalide'}
                      </span>
                    </td>
                    <td style={{ color: '#a0aec0', fontSize: '13px' }}>{account.soldAt ? new Date(account.soldAt).toLocaleDateString('fr-FR') : '-'}</td>
                    {/* Hide actions for MANAGER role */}
                    {adminRole !== 'MANAGER' && (
                      <td>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={() => handleEditAccount(account)}
                            style={{
                              background: '#2d3748',
                              color: '#667eea',
                              border: 'none',
                              padding: '8px 12px',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              fontSize: '13px',
                            }}
                            title="Modifier"
                          >
                            <Icon icon={LuPencil} size={14} />
                          </button>
                          <button
                            onClick={() => handleDeleteAccount(account.id)}
                            disabled={account.status === 'SOLD'}
                            style={{
                              background: account.status === 'SOLD' ? '#2d3748' : '#742a2a',
                              color: account.status === 'SOLD' ? '#4a5568' : '#fc8181',
                              border: 'none',
                              padding: '8px 12px',
                              borderRadius: '6px',
                              cursor: account.status === 'SOLD' ? 'not-allowed' : 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              fontSize: '13px',
                            }}
                            title={account.status === 'SOLD' ? 'Impossible de supprimer un compte vendu' : 'Supprimer'}
                          >
                            <Icon icon={LuTrash2} size={14} />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className={styles.emptyState}>
              {adminRole === 'MANAGER' ? 'Aucun compte en stock.' : 'Aucun compte en stock. Cliquez sur "Ajouter des comptes" pour commencer.'}
            </div>
          )}
        </div>

      {/* Edit Account Modal */}
      {showEditModal && editingAccount && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setShowEditModal(false)}
        >
          <div
            style={{
              background: '#1a202c',
              padding: '30px',
              borderRadius: '12px',
              maxWidth: '500px',
              width: '90%',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginTop: 0 }}>Modifier le compte</h2>
            <p style={{ color: '#a0aec0', fontSize: '14px', marginBottom: '20px' }}>
              Compte: <strong>{editingAccount.username}</strong>
            </p>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                Statut:
              </label>
              <select
                value={editStatus}
                onChange={(e) => setEditStatus(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '1px solid #2d3748',
                  background: '#0d1117',
                  color: 'white',
                  fontSize: '14px',
                }}
              >
                <option value="AVAILABLE">✅ Disponible</option>
                <option value="SOLD">💰 Vendu</option>
                <option value="RESERVED">🔒 Réservé</option>
                <option value="INVALID">❌ Invalide</option>
              </select>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                Notes (optionnel):
              </label>
              <textarea
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                placeholder="Ajouter des notes..."
                rows={4}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '1px solid #2d3748',
                  background: '#0d1117',
                  color: 'white',
                  fontSize: '13px',
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowEditModal(false)}
                style={{
                  padding: '12px 24px',
                  borderRadius: '8px',
                  border: '1px solid #2d3748',
                  background: 'transparent',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                Annuler
              </button>
              <button
                onClick={handleUpdateAccount}
                style={{
                  padding: '12px 24px',
                  borderRadius: '8px',
                  border: 'none',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '14px',
                }}
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Stock Modal */}
      {showAddModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setShowAddModal(false)}
        >
          <div
            style={{
              background: '#1a202c',
              padding: '30px',
              borderRadius: '12px',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '80vh',
              overflow: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginTop: 0 }}>{t('admin.addStock' as string) || 'Ajouter des comptes'}</h2>
            <p style={{ color: '#a0aec0', fontSize: '14px', marginBottom: '20px' }}>
              Format : <strong>username:password:2FA</strong><br/>
              1 ligne = 1 compte | 10 lignes = 10 comptes
            </p>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                Pays / Country:
              </label>
              <select
                value={addCountry}
                onChange={(e) => setAddCountry(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '1px solid #2d3748',
                  background: '#0d1117',
                  color: 'white',
                  fontSize: '14px',
                }}
              >
                <option value="FR">🇫🇷 France (FR)</option>
                <option value="US">🇺🇸 USA (US)</option>
              </select>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                Comptes (1 ligne = 1 compte):
              </label>
              <textarea
                value={csvInput}
                onChange={(e) => setCsvInput(e.target.value)}
                placeholder="username1:password1:ABC123&#10;username2:password2:DEF456&#10;username3:password3:GHI789"
                rows={12}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '1px solid #2d3748',
                  background: '#0d1117',
                  color: 'white',
                  fontFamily: 'monospace',
                  fontSize: '13px',
                  lineHeight: '1.6',
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowAddModal(false)}
                style={{
                  padding: '12px 24px',
                  borderRadius: '8px',
                  border: '1px solid #2d3748',
                  background: 'transparent',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                Annuler
              </button>
              <button
                onClick={handleAddStock}
                disabled={!csvInput.trim()}
                style={{
                  padding: '12px 24px',
                  borderRadius: '8px',
                  border: 'none',
                  background: csvInput.trim() ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#2d3748',
                  color: 'white',
                  cursor: csvInput.trim() ? 'pointer' : 'not-allowed',
                  fontWeight: '600',
                  fontSize: '14px',
                }}
              >
                Ajouter au stock
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default Stock;
